import { useQuery } from '@tanstack/react-query';
import { CenteredSpinner } from './Spinner.tsx';
import { EmptyState } from './EmptyState.tsx';
import { NavIcon } from './NavIcon.tsx';
import type { LibraryDocument } from '../lib/types.ts';

type ApiClient = { get<T>(path: string): Promise<T> };

// Shared read-only library list for the company and driver portals. The owning
// page supplies its authenticated api client + base path + a react-query key.
export function LibraryView({
  api,
  basePath,
  queryKey,
}: {
  api: ApiClient;
  basePath: string;
  queryKey: string[];
}) {
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => api.get<{ documents: LibraryDocument[] }>(basePath),
  });

  if (isLoading) return <CenteredSpinner />;
  const documents = data?.documents ?? [];

  async function download(id: string) {
    const res = await api.get<{ url: string }>(`${basePath}/${id}/download-url`).catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  // Group by category for a tidy presentation.
  const groups = new Map<string, LibraryDocument[]>();
  for (const d of documents) {
    const key = d.category ?? 'General';
    const list = groups.get(key) ?? [];
    list.push(d);
    groups.set(key, list);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Document library</h1>
        <p className="mt-1 text-sm text-slate-500">
          Handbooks, policies and inductions to read. These are reference documents — nothing to
          fill in here.
        </p>
      </div>

      {documents.length === 0 ? (
        <EmptyState
          title="No documents yet"
          description="When documents are published for you, they will appear here."
        />
      ) : (
        <div className="space-y-6">
          {[...groups.entries()].map(([category, docs]) => (
            <div key={category}>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {category}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {docs.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => void download(d.id)}
                    className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    <NavIcon name="file-text" size={20} style={{ color: '#64748B' }} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-900">{d.title}</div>
                      {d.description && (
                        <div className="mt-0.5 text-xs text-slate-500">{d.description}</div>
                      )}
                      <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-blue-600">
                        Open ↗
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
